import React, { useState, useEffect } from 'react';
import BottomSheet from './BottomSheet';
import { typesOfWorkV2Api, TypeOfWorkV2 } from '../../../api/typesOfWorkV2Api';
import HorizontalCategoryTabs from '../HorizontalCategoryTabs';
import './WorkTypeSelector.css';

// Кэш для типов работ на уровне модуля (общий для всех экземпляров компонента)
let cachedTypesOfWork: TypeOfWorkV2[] | null = null;
let loadingPromise: Promise<TypeOfWorkV2[]> | null = null;

// Функция для очистки кэша (если нужно обновить данные)
export const clearWorkTypesCache = () => {
  cachedTypesOfWork = null;
  loadingPromise = null;
};

export interface WorkTypeSelectorProps {
  value: number | null;
  onChange: (typeId: number | null) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  id?: string;
  name?: string;
  compact?: boolean;  // Компактное отображение без категории
}

const WorkTypeSelector: React.FC<WorkTypeSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Выберите тип работы',
  label,
  required = false,
  disabled = false,
  error,
  className = '',
  id,
  name,
  compact = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWorkV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Загружаем типы работ с кэшированием
  useEffect(() => {
    const loadTypesOfWork = async () => {
      // Если данные уже в кэше, используем их сразу
      if (cachedTypesOfWork) {
        setTypesOfWork(cachedTypesOfWork);
        return;
      }

      // Если уже идет загрузка, ждем её завершения
      if (loadingPromise) {
        try {
          const data = await loadingPromise;
          setTypesOfWork(data);
        } catch (error) {
          console.error('Ошибка загрузки типов работ:', error);
        }
        return;
      }

      // Загружаем данные и кэшируем
      setLoading(true);
      loadingPromise = typesOfWorkV2Api.getTypesOfWork();
      
      try {
        const data = await loadingPromise;
        cachedTypesOfWork = data;
        setTypesOfWork(data);
      } catch (error) {
        console.error('Ошибка загрузки типов работ:', error);
        loadingPromise = null; // Сбрасываем промис при ошибке, чтобы можно было повторить
      } finally {
        setLoading(false);
        loadingPromise = null;
      }
    };

    loadTypesOfWork();
  }, []);

  // Получаем уникальные категории
  const categories = Array.from(new Set(typesOfWork.map(type => type.section)));

  // Фильтруем типы работ по поиску и категории
  const filteredTypes = typesOfWork.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         type.section.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || type.section === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Группируем по категориям
  const groupedTypes = filteredTypes.reduce((acc, type) => {
    if (!acc[type.section]) {
      acc[type.section] = [];
    }
    acc[type.section].push(type);
    return acc;
  }, {} as Record<string, TypeOfWorkV2[]>);

  // Получаем выбранный тип работы
  const selectedType = typesOfWork.find(type => type.id === value);

  const handleSelect = (typeId: number) => {
    onChange(typeId);
    setIsOpen(false);
    setSearchQuery('');
    setSelectedCategory(null);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setSelectedCategory(null);
  };

  const handleSelectorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={`work-type-selector ${className}`} onClick={handleSelectorClick}>
      {label && (
        <label htmlFor={id} className="work-type-selector-label">
          {label}
          {required && <span className="required"> *</span>}
        </label>
      )}

      {/* Кнопка выбора */}
      <div 
        className={`work-type-selector-button ${disabled ? 'disabled' : ''} ${error ? 'error' : ''} ${compact ? 'compact' : ''}`}
        onClick={handleToggle}
        id={id}
      >
        <div className="work-type-selector-content">
          {selectedType ? (
            <div className="work-type-selected">
              <div className="work-type-name">{selectedType.name}</div>
              {!compact && <div className="work-type-category">{selectedType.section}</div>}
            </div>
          ) : (
            <span className="work-type-placeholder">{placeholder}</span>
          )}
        </div>
        <div className="work-type-selector-arrow">
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line`}></i>
        </div>
      </div>

      {/* Bottom Sheet для выбора типа работы */}
      <BottomSheet
        isOpen={isOpen}
        onClose={handleClose}
        title="Выберите тип работы"
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="work-type-selector-content">
            {/* Поиск */}
            <div className="work-type-search">
              <div className="search-input-wrapper">
                <i className="ri-search-line search-icon"></i>
                <input
                  type="text"
                  placeholder="Поиск по названию или категории..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="search-clear"
                    onClick={() => setSearchQuery('')}
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Фильтр по категориям */}
            {categories.length > 0 && (
              <HorizontalCategoryTabs
                categories={categories}
                value={selectedCategory}
                onChange={setSelectedCategory}
                showAllOption={true}
                allOptionLabel="Все категории"
                size="medium"
              />
            )}

            {/* Список типов работ */}
            <div className="work-type-list">
              {loading ? (
                <div className="work-type-loading">
                  <i className="ri-loader-4-line ri-spin"></i>
                  <span>Загрузка типов работ...</span>
                </div>
              ) : Object.keys(groupedTypes).length === 0 ? (
                <div className="work-type-empty">
                  <i className="ri-search-line"></i>
                  <span>Типы работ не найдены</span>
                </div>
              ) : (
                Object.entries(groupedTypes).map(([category, types]) => (
                  <div key={category} className="work-type-category-group">
                    <div className="category-header">
                      <h4>{category}</h4>
                      <span className="category-count">{types.length}</span>
                    </div>
                    <div className="work-type-grid">
                      {types.map(type => (
                        <div
                          key={type.id}
                          className={`work-type-card ${value === type.id ? 'selected' : ''}`}
                          onClick={() => handleSelect(type.id)}
                        >
                          <div className="work-type-card-content">
                            <div className="work-type-card-name">{type.name}</div>
                          </div>
                          {value === type.id && (
                            <div className="work-type-card-check">
                              <i className="ri-check-line"></i>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </BottomSheet>

      {/* Ошибка */}
      {error && (
        <div className="work-type-error">
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default WorkTypeSelector;
